import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './components/Login';
import Layout from './components/layout/Layout';
import HomePage from './components/HomePage';
import { TodoPlaceholder } from './components/todos/TodoPlaceholder';
import { NutritionPage } from './components/nutrition/NutritionPage';
import { IntegrationSettings } from './components/settings';
import { BudgetPage } from './components/budget';
import { AdminProtectedRoute, AgentTaskViewer } from './components/admin';
import ProtectedRoute from './pages/ProtectedRoute';
import RecipeDetailPage from './pages/RecipeDetailPage';
import RecipeFormPage from './pages/RecipeFormPage';
import RecipeListPage from './pages/RecipeListPage';
import ContactListPage from './pages/ContactListPage';
import ContactDetailPage from './pages/ContactDetailPage';
import ContactFormPage from './pages/ContactFormPage';
import SkippedEmailsPage from './pages/SkippedEmailsPage';

const AppContent: React.FC = () => {
  const { loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/recipes/*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<RecipeListPage />} />
                <Route
                  path="/new"
                  element={
                    <ProtectedRoute>
                      <RecipeFormPage mode="create" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recipe/:id"
                  element={
                    <ProtectedRoute>
                      <RecipeDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/recipe/:id/edit"
                  element={
                    <ProtectedRoute>
                      <RecipeFormPage mode="edit" />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Layout>
          }
        />
        <Route
          path="/todos/*"
          element={
            <Layout>
              <ProtectedRoute>
                <TodoPlaceholder />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/nutrition/*"
          element={
            <Layout>
              <ProtectedRoute>
                <NutritionPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/contacts/*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<ContactListPage />} />
                <Route
                  path="/new"
                  element={
                    <ProtectedRoute>
                      <ContactFormPage mode="create" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:id"
                  element={
                    <ProtectedRoute>
                      <ContactDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:id/edit"
                  element={
                    <ProtectedRoute>
                      <ContactFormPage mode="edit" />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Layout>
          }
        />
        <Route
          path="/settings/gmail/skipped"
          element={
            <Layout>
              <ProtectedRoute>
                <SkippedEmailsPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/settings/*"
          element={
            <Layout>
              <ProtectedRoute>
                <IntegrationSettings />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/budget/*"
          element={
            <Layout>
              <ProtectedRoute>
                <BudgetPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route path="/admin" element={<Navigate to="/admin/agent-tasks" replace />} />
        <Route
          path="/admin/agent-tasks"
          element={
            <AdminProtectedRoute>
              <Layout>
                <AgentTaskViewer />
              </Layout>
            </AdminProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AdminProvider>
            <AppContent />
          </AdminProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
